import React, { useEffect, useRef, useState, useCallback } from "react";
import {
  Shield,
  Users,
  BarChart3,
  FolderKey,
  KeyRound,
  UserCheck,
  MapPin,
  Plug,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";

/**
 * TabNavigation component for Field Configuration
 * Displays tabs for Licenses, Roles, and Users
 * @param {Object} props
 * @param {string} props.activeTab - Currently active tab ('licenses', 'roles', or 'users')
 * @param {Function} props.onTabChange - Callback when tab is clicked, receives tab key
 */
export default function TabNavigation({ activeTab, onTabChange, allowedTabs }) {
  const allTabs = [
    { key: "licenses", label: "Licenses", icon: BarChart3 },
    { key: "roles", label: "Roles", icon: Shield },
    { key: "users", label: "Users", icon: Users },
    { key: "access_groups", label: "Access Groups", icon: FolderKey },
    { key: "approval_teams", label: "Approval Teams", icon: UserCheck },
    { key: "job_locations", label: "Job Locations", icon: MapPin },
    { key: "api_keys", label: "API Management", icon: KeyRound },
    { key: "integrations", label: "Integrations", icon: Plug },
  ];

  const tabs = allowedTabs
    ? allTabs.filter((tab) => allowedTabs.includes(tab.key))
    : allTabs;

  const scrollRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const updateScrollState = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 1);
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 1);
  }, []);

  useEffect(() => {
    updateScrollState();
    window.addEventListener("resize", updateScrollState);
    return () => window.removeEventListener("resize", updateScrollState);
  }, [updateScrollState, tabs.length]);

  const scrollBy = (amount) => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollBy({ left: amount, behavior: "smooth" });
  };

  return (
    <div className="field-configuration__tabs-wrapper">
      {canScrollLeft && (
        <button
          type="button"
          className="field-configuration__tabs-arrow field-configuration__tabs-arrow--left"
          onClick={() => scrollBy(-160)}
          aria-label="Scroll tabs left"
        >
          <ChevronLeft size={18} />
        </button>
      )}
      <div
        className="field-configuration__tabs"
        ref={scrollRef}
        onScroll={updateScrollState}
      >
        {tabs.map(({ key, label, icon: Icon }) => (
          <button
            key={key}
            onClick={() => onTabChange(key)}
            className={`field-configuration__tab ${
              activeTab === key ? "field-configuration__tab--active" : ""
            }`}
          >
            <Icon size={18} />
            {label}
          </button>
        ))}
      </div>
      {canScrollRight && (
        <button
          type="button"
          className="field-configuration__tabs-arrow field-configuration__tabs-arrow--right"
          onClick={() => scrollBy(160)}
          aria-label="Scroll tabs right"
        >
          <ChevronRight size={18} />
        </button>
      )}
    </div>
  );
}
